import {
  AfterViewInit, Component, Directive, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy,
  OnInit, Output, ViewChild, ViewEncapsulation
} from '@angular/core';

let nextId = 0;
/** Open modals, innermost last: only the top one answers Escape and traps focus. */
const openStack: LbModalComponent[] = [];

/** Marks the element that becomes the modal footer: <div lbModalFooter>buttons</div>. */
@Directive({ selector: '[lbModalFooter]' })
export class LbModalFooterDirective {
  @HostBinding('class.modal-footer') footer = true;
}

/**
 * The one modal for the back office (replaces the hand-rolled
 * <div class="modal d-block" style="background: rgba(0,0,0,0.5)"> copies).
 *
 * Slots: [lbModalTitle] (rich title), [lbModalToolbar] (fixed, between header and the
 * scrolling body), [lbModalFooter].
 *
 * Render it with *ngIf and close it from (closed):
 *   <lb-modal *ngIf="showEdit" title="Edit vendor" size="lg" (closed)="showEdit = false">
 *     ...body...
 *     <div lbModalFooter><button ...>Save</button></div>
 *   </lb-modal>
 *
 * Gives every dialog the same header, close button, sizes, a scrolling body with the
 * header and footer kept in view, Escape and backdrop close, a focus trap, focus
 * returned to what opened it, no page scroll behind it, and full screen on phones.
 */
@Component({
  selector: 'lb-modal',
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="modal-backdrop show lb-modal-backdrop" [style.z-index]="zIndex - 5"></div>
    <div class="modal d-block lb-modal" tabindex="-1" [style.z-index]="zIndex" (mousedown)="onBackdropMouseDown($event)">
      <div #dialog class="modal-dialog modal-dialog-scrollable modal-fullscreen-sm-down" [ngClass]="dialogClass"
           role="dialog" aria-modal="true" [attr.aria-labelledby]="titleId" tabindex="-1">
        <div class="modal-content">
          <div class="modal-header" [ngClass]="toneClass">
            <div class="lb-modal-heading">
              <h2 class="modal-title lb-modal-title" [id]="titleId">
                <i *ngIf="icon" class="fa me-1" [ngClass]="icon" aria-hidden="true"></i>{{ title }}<ng-content select="[lbModalTitle]"></ng-content>
              </h2>
              <div class="lb-modal-subtitle" *ngIf="subtitle">{{ subtitle }}</div>
            </div>
            <button type="button" class="btn-close" aria-label="Close"
                    (click)="requestClose()" [disabled]="busy"></button>
          </div>
          <ng-content select="[lbModalToolbar]"></ng-content>
          <div class="modal-body" [ngClass]="bodyClass">
            <ng-content></ng-content>
          </div>
          <ng-content select="[lbModalFooter]"></ng-content>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .lb-modal .lb-modal-title {
      font-size: 1.15rem;
      font-weight: 600;
      margin: 0;
    }
    .lb-modal .modal-header {
      gap: 0.75rem;
      align-items: flex-start;
    }
    /* Headers stay neutral; a tone only colours the title icon */
    .lb-modal .lb-modal-tone--bad .lb-modal-title .fa { color: var(--lb-bad); }
    .lb-modal .lb-modal-tone--ok .lb-modal-title .fa { color: var(--lb-ok); }
    .lb-modal .lb-modal-tone--warn .lb-modal-title .fa { color: var(--lb-warn); }
    .lb-modal .lb-modal-heading {
      min-width: 0;
    }
    .lb-modal .lb-modal-subtitle {
      font-size: 0.875rem;
      color: var(--lb-text-muted);
      margin-top: 0.15rem;
    }
    .lb-modal .modal-footer {
      gap: 0.5rem;
    }
    .lb-modal .modal-footer > * {
      margin: 0;
    }
    body.lb-modal-open {
      overflow: hidden;
    }
  `]
})
export class LbModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() title = '';
  /** Font Awesome icon class, e.g. "fa-truck" */
  @Input() icon = '';
  @Input() subtitle = '';
  /** Extra classes for the scrolling body, e.g. "p-0" for an edge-to-edge table */
  @Input() bodyClass = '';
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() centered = false;
  /**
   * The dialog's tone. Headers are always neutral (DESIGN.md: colour only means status):
   * "bg-danger"/"bg-success"/"bg-warning" (the old header colours) now tint only the
   * title icon. Prefer tone="bad" | "ok" | "warn" in new code.
   */
  @Input() headerClass = '';
  @Input() tone: '' | 'bad' | 'ok' | 'warn' = '';
  /** No longer used: headers are never dark. Kept so existing templates still compile. */
  @Input() lightClose = false;
  /** Clicking outside the dialog closes it. Switch off for forms where a stray click would lose work. */
  @Input() closeOnBackdrop = true;
  @Input() closeOnEscape = true;
  /** While true (e.g. saving), nothing closes the dialog */
  @Input() busy = false;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('dialog', { static: true }) dialog!: ElementRef<HTMLElement>;

  readonly titleId = `lb-modal-title-${++nextId}`;
  zIndex = 1055;
  private returnFocus: HTMLElement | null = null;

  constructor(private host: ElementRef<HTMLElement>) { }

  get toneClass(): string {
    let tone = this.tone;
    if (!tone && this.headerClass) {
      if (this.headerClass.indexOf('bg-danger') >= 0) tone = 'bad';
      else if (this.headerClass.indexOf('bg-success') >= 0) tone = 'ok';
      else if (this.headerClass.indexOf('bg-warning') >= 0) tone = 'warn';
    }
    return tone ? 'lb-modal-tone--' + tone : '';
  }

  get dialogClass(): string {
    const size = this.size === 'md' ? '' : `modal-${this.size}`;
    return `${size}${this.centered ? ' modal-dialog-centered' : ''}`;
  }

  ngOnInit(): void {
    this.returnFocus = document.activeElement as HTMLElement | null;
    openStack.push(this);
    this.zIndex = 1055 + (openStack.length - 1) * 10;
    document.body.classList.add('lb-modal-open');
  }

  ngAfterViewInit(): void {
    // After the body has rendered: an [autofocus] field if there is one, else the dialog itself
    setTimeout(() => {
      const preferred = this.host.nativeElement.querySelector<HTMLElement>('.modal-body [autofocus]');
      (preferred || this.dialog.nativeElement).focus();
    });
  }

  ngOnDestroy(): void {
    const index = openStack.indexOf(this);
    if (index >= 0) openStack.splice(index, 1);
    if (openStack.length === 0) document.body.classList.remove('lb-modal-open');
    const target = this.returnFocus;
    if (target && document.body.contains(target)) setTimeout(() => target.focus());
  }

  requestClose(): void {
    if (!this.busy) this.closed.emit();
  }

  onBackdropMouseDown(event: MouseEvent): void {
    // Only a press on the dimmed area itself, not one that started inside the dialog
    if (this.closeOnBackdrop && event.target === event.currentTarget) this.requestClose();
  }

  private get isTop(): boolean {
    return openStack[openStack.length - 1] === this;
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.isTop) return;
    if (event.key === 'Escape' && this.closeOnEscape) {
      event.preventDefault();
      this.requestClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusables = Array.from(this.dialog.nativeElement.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(el => el.offsetParent !== null);
    if (focusables.length === 0) {
      event.preventDefault();
      this.dialog.nativeElement.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;
    const inside = !!active && this.dialog.nativeElement.contains(active);
    if (event.shiftKey && (active === first || !inside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !inside)) {
      event.preventDefault();
      first.focus();
    }
  }
}
